/**
 * TeamSignalDashboard - Founder Signal Layer
 * 
 * Displays anonymized, aggregated team signals:
 * - Strategic Layer: Momentum, Experimentation, Decisions, Blockers
 * - Signal Layer: Sentiment, Confidence, Overload, Ownership Friction
 * - AI Insights: Actionable recommendations
 * 
 * Privacy by design:
 * - No individual attribution
 * - No raw reflection text
 * - Minimum 3 members required
 */
import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  Minus,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  Zap,
  Target,
  Users,
  Brain,
  Lightbulb,
  BarChart3,
  RefreshCw,
  Loader2,
  ChevronUp,
  ChevronDown,
  Info
} from 'lucide-react';
import { fetchTeamSignals, fetchSignalHistory, generateTeamSignals } from '../../services/teamsService';

// Risk level colors and icons
const RISK_CONFIG = {
  low: { color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30', icon: CheckCircle },
  moderate: { color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30', icon: AlertCircle },
  elevated: { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', icon: AlertTriangle }
};

// Momentum labels
const MOMENTUM_CONFIG = {
  rising: { color: 'text-green-400', icon: TrendingUp },
  stable: { color: 'text-zinc-400', icon: Minus },
  slowing: { color: 'text-amber-400', icon: TrendingDown }
};

// Experimentation labels
const EXPERIMENTATION_CONFIG = {
  high: { color: 'text-green-400', label: 'High' },
  medium: { color: 'text-zinc-400', label: 'Medium' },
  low: { color: 'text-amber-400', label: 'Low' }
};

// Ownership friction labels
const FRICTION_CONFIG = {
  low: { color: 'text-green-400' },
  moderate: { color: 'text-amber-400' },
  elevated: { color: 'text-red-400' }
};

const TeamSignalDashboard = ({ teamId, teamName, isAdmin }) => {
  const [signals, setSignals] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (teamId && isAdmin) {
      loadSignals();
    }
  }, [teamId, isAdmin]);

  const loadSignals = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [signalsData, historyData] = await Promise.all([
        fetchTeamSignals(teamId),
        fetchSignalHistory(teamId, 4)
      ]);
      
      setSignals(signalsData);
      setHistory(historyData?.history || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSignals = async () => {
    setGenerating(true);
    try {
      await generateTeamSignals(teamId);
      await loadSignals();
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  // Not admin - don't show
  if (!isAdmin) {
    return null;
  }

  // Loading state
  if (loading) {
    return (
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
        <div className="flex items-center justify-center gap-3 text-zinc-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading team signals...</span>
        </div>
      </div>
    );
  }

  // Error state (including not enough members)
  if (error) {
    return (
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-zinc-500 mt-0.5" />
          <div>
            <h3 className="text-white font-medium mb-1">Signal Dashboard</h3>
            <p className="text-zinc-500 text-sm">{error}</p>
            {error.includes('members') && (
              <p className="text-zinc-600 text-xs mt-2">
                Invite more team members to unlock anonymized team insights.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // No signals yet
  if (!signals) {
    return (
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
        <div className="text-center">
          <BarChart3 className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
          <h3 className="text-white font-medium mb-1">No Signals Yet</h3>
          <p className="text-zinc-500 text-sm mb-4">
            Signals are generated weekly from team reflections.
          </p>
          <button
            onClick={handleGenerateSignals}
            disabled={generating}
            className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg text-sm transition-colors"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Generate Now
          </button>
        </div>
      </div>
    );
  }

  const riskConfig = RISK_CONFIG[signals.risk_level] || RISK_CONFIG.low;
  const RiskIcon = riskConfig.icon;
  const momentumConfig = MOMENTUM_CONFIG[signals.momentum_label] || MOMENTUM_CONFIG.stable;
  const MomentumIcon = momentumConfig.icon;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-violet-400" />
          <h2 className="text-lg font-medium text-white">Team Signals</h2>
          <span className="text-xs text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded">
            Week of {signals.week_start}
          </span>
        </div>
        <button
          onClick={handleGenerateSignals}
          disabled={generating}
          className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
          title="Refresh signals"
        >
          {generating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Risk Banner */}
      <div className={`${riskConfig.bg} ${riskConfig.border} border rounded-lg p-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <RiskIcon className={`w-5 h-5 ${riskConfig.color}`} />
            <div>
              <span className={`font-medium ${riskConfig.color}`}>
                {signals.risk_level.charAt(0).toUpperCase() + signals.risk_level.slice(1)} Risk
              </span>
              <p className="text-zinc-400 text-sm">
                {signals.member_count} members contributing this week
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Strategic Layer */}
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-4">
        <h3 className="text-xs font-medium text-zinc-500 uppercase tracking-wide mb-3">
          Strategic
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Momentum */}
          <div className="bg-zinc-800/50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <MomentumIcon className={`w-4 h-4 ${momentumConfig.color}`} />
              <span className="text-xs text-zinc-500">Momentum</span>
            </div>
            <p className={`text-lg font-semibold ${momentumConfig.color}`}>
              {signals.momentum_label.charAt(0).toUpperCase() + signals.momentum_label.slice(1)}
            </p>
            <p className="text-xs text-zinc-600">Index: {signals.momentum_index}</p>
          </div>

          {/* Experimentation */}
          <div className="bg-zinc-800/50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-4 h-4 text-zinc-400" />
              <span className="text-xs text-zinc-500">Experimentation</span>
            </div>
            <p className={`text-lg font-semibold ${EXPERIMENTATION_CONFIG[signals.experimentation_label]?.color || 'text-zinc-400'}`}>
              {EXPERIMENTATION_CONFIG[signals.experimentation_label]?.label || signals.experimentation_label}
            </p>
            <p className="text-xs text-zinc-600">{signals.experimentation_rate}/member</p>
          </div>

          {/* Decisions */}
          <div className="bg-zinc-800/50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Target className="w-4 h-4 text-zinc-400" />
              <span className="text-xs text-zinc-500">Decisions</span>
            </div>
            <p className="text-lg font-semibold text-white">
              {signals.decision_count}
            </p>
            <p className="text-xs text-zinc-600">this week</p>
          </div>

          {/* Top Blockers */}
          <div className="bg-zinc-800/50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle className="w-4 h-4 text-zinc-400" />
              <span className="text-xs text-zinc-500">Top Blockers</span>
            </div>
            {signals.top_blockers?.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {signals.top_blockers.slice(0, 2).map((blocker, idx) => (
                  <span 
                    key={idx}
                    className="text-xs bg-zinc-700 text-zinc-300 px-2 py-0.5 rounded"
                  >
                    {blocker.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-zinc-500">None identified</p>
            )}
          </div>
        </div>
      </div>

      {/* Signal Layer */}
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-4">
        <h3 className="text-xs font-medium text-zinc-500 uppercase tracking-wide mb-3">
          Signals
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Confidence */}
          <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
            <div>
              <p className="text-xs text-zinc-500">Confidence</p>
              <p className="text-sm font-medium text-white capitalize">
                {signals.confidence_trend}
              </p>
            </div>
            {signals.confidence_trend === 'increasing' && <ChevronUp className="w-5 h-5 text-green-400" />}
            {signals.confidence_trend === 'decreasing' && <ChevronDown className="w-5 h-5 text-red-400" />}
            {signals.confidence_trend === 'stable' && <Minus className="w-5 h-5 text-zinc-400" />}
          </div>

          {/* Sentiment */}
          <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
            <div>
              <p className="text-xs text-zinc-500">Sentiment</p>
              <p className={`text-sm font-medium ${
                signals.avg_sentiment > 0.2 ? 'text-green-400' : 
                signals.avg_sentiment < -0.2 ? 'text-red-400' : 'text-zinc-400'
              }`}>
                {signals.avg_sentiment > 0 ? '+' : ''}{signals.avg_sentiment.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Overload */}
          <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
            <div>
              <p className="text-xs text-zinc-500">Overload</p>
              <p className={`text-sm font-medium ${
                signals.overload_count > signals.member_count * 0.4 ? 'text-red-400' :
                signals.overload_count > 0 ? 'text-amber-400' : 'text-green-400'
              }`}>
                {signals.overload_count}/{signals.member_count} flagged
              </p>
            </div>
          </div>

          {/* Ownership Friction */}
          <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
            <div>
              <p className="text-xs text-zinc-500">Ownership Friction</p>
              <p className={`text-sm font-medium capitalize ${
                FRICTION_CONFIG[signals.ownership_friction_level]?.color || 'text-zinc-400'
              }`}>
                {signals.ownership_friction_level}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* AI Insights */}
      {signals.ai_insights?.length > 0 && (
        <div className="bg-gradient-to-r from-violet-500/10 to-blue-500/10 rounded-xl border border-violet-500/20 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="w-4 h-4 text-violet-400" />
            <h3 className="text-sm font-medium text-white">AI Insights</h3>
          </div>
          <div className="space-y-2">
            {signals.ai_insights.map((insight, idx) => (
              <p key={idx} className="text-sm text-zinc-300 pl-6 relative">
                <span className="absolute left-0 text-violet-400">•</span>
                {insight}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Privacy Notice */}
      <p className="text-xs text-zinc-600 text-center">
        Signals are anonymized and aggregated. No individual attribution.
      </p>
    </div>
  );
};

export default TeamSignalDashboard;
